// Videos Tab - Vocabulary Video Generator
// Generate educational Arabic-English vocabulary videos

import { useState, useCallback, useEffect } from "react";
import styles from "./VideosTab.module.css";

// Modal video generator API URL
const VIDEO_API_URL = "https://digitixllc--vocabulary-video-generator-web-app.modal.run";

interface VocabularyItem {
  arabic: string;
  english: string;
  emoji?: string;
  emoji_category?: string;
}

interface VideoContent {
  title: string;
  subtitle?: string;
  items: VocabularyItem[];
}

interface VideoJob {
  id: string;
  status: "pending" | "generating_content" | "rendering" | "completed" | "failed";
  progress: number;
  error?: string;
  video_url?: string;
  manifest_url?: string;
  preview_url?: string;
}

const EMOJI_CATEGORIES = [
  "airport", "travel", "transport", "food", "home", "work",
  "education", "health", "technology", "people", "nature", "general"
];

export function VideosTab() {
  const [mode, setMode] = useState<"topic" | "manual">("topic");
  const [topic, setTopic] = useState("");
  const [manualContent, setManualContent] = useState<VideoContent>({
    title: "",
    subtitle: "",
    items: [
      { arabic: "", english: "", emoji_category: "general" },
      { arabic: "", english: "", emoji_category: "general" },
      { arabic: "", english: "", emoji_category: "general" },
      { arabic: "", english: "", emoji_category: "general" },
      { arabic: "", english: "", emoji_category: "general" },
    ],
  });
  const [jobs, setJobs] = useState<VideoJob[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollingJobs, setPollingJobs] = useState<Set<string>>(new Set());

  // Load jobs from API
  const loadJobs = useCallback(async () => {
    try {
      const response = await fetch(`${VIDEO_API_URL}/jobs`);
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error("Failed to load jobs:", err);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Poll for job status updates
  useEffect(() => {
    if (pollingJobs.size === 0) return;

    const interval = setInterval(async () => {
      for (const jobId of pollingJobs) {
        try {
          const response = await fetch(`${VIDEO_API_URL}/jobs/${jobId}`);
          if (response.ok) {
            const job = await response.json();
            setJobs(prev => prev.map(j => j.id === jobId ? job : j));

            // Stop polling if job is done
            if (job.status === "completed" || job.status === "failed") {
              setPollingJobs(prev => {
                const next = new Set(prev);
                next.delete(jobId);
                return next;
              });
            }
          }
        } catch (err) {
          console.error(`Failed to poll job ${jobId}:`, err);
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [pollingJobs]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const body: { topic?: string; content?: VideoContent } = {};

      if (mode === "topic") {
        if (!topic.trim() || topic.length < 10) {
          throw new Error("Topic must be at least 10 characters");
        }
        body.topic = topic;
      } else {
        // Validate manual content
        if (!manualContent.title.trim()) {
          throw new Error("Title is required");
        }
        const validItems = manualContent.items.filter(
          item => item.arabic.trim() && item.english.trim()
        );
        if (validItems.length < 5) {
          throw new Error("At least 5 vocabulary items are required");
        }
        body.content = {
          ...manualContent,
          items: validItems,
        };
      }

      const response = await fetch(`${VIDEO_API_URL}/jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to create job");
      }

      const job = await response.json();
      setJobs(prev => [job, ...prev]);
      setPollingJobs(prev => new Set(prev).add(job.id));

      // Clear form
      if (mode === "topic") {
        setTopic("");
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create video job");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddItem = () => {
    if (manualContent.items.length >= 12) return;
    setManualContent(prev => ({
      ...prev,
      items: [...prev.items, { arabic: "", english: "", emoji_category: "general" }],
    }));
  };

  const handleRemoveItem = (index: number) => {
    if (manualContent.items.length <= 5) return;
    setManualContent(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleItemChange = (index: number, field: keyof VocabularyItem, value: string) => {
    setManualContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return styles.statusCompleted;
      case "failed": return styles.statusFailed;
      case "rendering": return styles.statusRendering;
      default: return styles.statusPending;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "pending": return "Pending";
      case "generating_content": return "Generating Content";
      case "rendering": return "Rendering Video";
      case "completed": return "Completed";
      case "failed": return "Failed";
      default: return status;
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Vocabulary Video Generator</h1>
        <p className={styles.subtitle}>
          Create educational Arabic-English vocabulary videos with AI narration
        </p>
      </div>

      {/* Mode Toggle */}
      <div className={styles.modeToggle}>
        <button
          className={`${styles.modeBtn} ${mode === "topic" ? styles.active : ""}`}
          onClick={() => setMode("topic")}
        >
          Generate from Topic
        </button>
        <button
          className={`${styles.modeBtn} ${mode === "manual" ? styles.active : ""}`}
          onClick={() => setMode("manual")}
        >
          Manual Input
        </button>
      </div>

      {/* Input Form */}
      <div className={styles.formCard}>
        {mode === "topic" ? (
          <div className={styles.topicForm}>
            <label className={styles.label}>Topic</label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g., English vocabulary about airports for Arabic speakers"
              className={styles.input}
            />
            <p className={styles.hint}>
              Describe the vocabulary topic you want to create a video for.
              AI will generate 8-10 relevant vocabulary pairs.
            </p>
          </div>
        ) : (
          <div className={styles.manualForm}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Arabic Title</label>
                <input
                  type="text"
                  value={manualContent.title}
                  onChange={(e) => setManualContent(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="تعلم اللغة الإنجليزية"
                  className={styles.input}
                  dir="rtl"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>English Subtitle</label>
                <input
                  type="text"
                  value={manualContent.subtitle || ""}
                  onChange={(e) => setManualContent(prev => ({ ...prev, subtitle: e.target.value }))}
                  placeholder="Airport Vocabulary"
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.itemsHeader}>
              <label className={styles.label}>Vocabulary Items (5-12)</label>
              <button
                onClick={handleAddItem}
                disabled={manualContent.items.length >= 12}
                className={styles.addBtn}
              >
                + Add Item
              </button>
            </div>

            <div className={styles.itemsList}>
              {manualContent.items.map((item, index) => (
                <div key={index} className={styles.itemRow}>
                  <span className={styles.itemNumber}>{index + 1}</span>
                  <input
                    type="text"
                    value={item.arabic}
                    onChange={(e) => handleItemChange(index, "arabic", e.target.value)}
                    placeholder="Arabic word"
                    className={styles.itemInput}
                    dir="rtl"
                  />
                  <input
                    type="text"
                    value={item.english}
                    onChange={(e) => handleItemChange(index, "english", e.target.value)}
                    placeholder="English translation"
                    className={styles.itemInput}
                  />
                  <select
                    value={item.emoji_category || "general"}
                    onChange={(e) => handleItemChange(index, "emoji_category", e.target.value)}
                    className={styles.categorySelect}
                  >
                    {EMOJI_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleRemoveItem(index)}
                    disabled={manualContent.items.length <= 5}
                    className={styles.removeBtn}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className={styles.error}>{error}</div>
        )}

        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={styles.submitBtn}
        >
          {isSubmitting ? "Creating..." : "Generate Video"}
        </button>
      </div>

      {/* Jobs List */}
      <div className={styles.jobsSection}>
        <h2 className={styles.sectionTitle}>Video Jobs</h2>

        {jobs.length === 0 ? (
          <div className={styles.emptyState}>
            No video jobs yet. Create one above!
          </div>
        ) : (
          <div className={styles.jobsList}>
            {jobs.map((job) => (
              <div key={job.id} className={styles.jobCard}>
                <div className={styles.jobHeader}>
                  <span className={styles.jobId}>#{job.id}</span>
                  <span className={`${styles.status} ${getStatusColor(job.status)}`}>
                    {getStatusText(job.status)}
                  </span>
                </div>

                {job.status === "rendering" && (
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${job.progress}%` }}
                    />
                  </div>
                )}

                {job.error && (
                  <div className={styles.jobError}>{job.error}</div>
                )}

                {job.status === "completed" && (
                  <div className={styles.jobResults}>
                    {job.preview_url && (
                      <img
                        src={job.preview_url}
                        alt="Video preview"
                        className={styles.preview}
                      />
                    )}
                    <div className={styles.jobActions}>
                      {job.video_url && (
                        <a
                          href={job.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.downloadBtn}
                        >
                          Download Video
                        </a>
                      )}
                      {job.manifest_url && (
                        <a
                          href={job.manifest_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.manifestBtn}
                        >
                          View Manifest
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
